import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Building2,
  ArrowLeft,
  Edit,
  Save,
  Users,
  Mail,
  Calendar,
  Gift,
  Trash2,
  UserPlus,
  Key,
  Ban,
  CheckCircle,
  XCircle,
  Loader2,
  Crown,
  Clock,
  MoreVertical,
  AlertTriangle,
  Tag,
  Sparkles,
  DollarSign,
  ToggleLeft,
  ToggleRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import axios from 'axios';
import { ADMIN_BASE_URL } from '../config/admin';

const API = process.env.REACT_APP_BACKEND_URL;

// Plan pricing
const PLANS = {
  trial: {
    name: 'Trial',
    basePrice: 0,
    seatsIncluded: 5,
    extraSeatPrice: 0
  },
  professional: {
    name: 'Professional',
    basePrice: 299,
    seatsIncluded: 5,
    extraSeatPrice: 29
  },
  enterprise: {
    name: 'Enterprise',
    basePrice: 599,
    seatsIncluded: 15,
    extraSeatPrice: 25
  }
};

export default function CompanyDetail() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Dialogs
  const [showAddUserDialog, setShowAddUserDialog] = useState(false);
  const [showCompDialog, setShowCompDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showCouponDialog, setShowCouponDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  
  // Promotions/Coupons
  const [availablePromotions, setAvailablePromotions] = useState([]);
  const [assignedPromotions, setAssignedPromotions] = useState([]);
  const [selectedPromotion, setSelectedPromotion] = useState('');
  
  // Form states
  const [editForm, setEditForm] = useState({});
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user'
  });
  const [compSettings, setCompSettings] = useState({
    is_comped: false,
    comp_until: '',
    comp_forever: false,
    comp_reason: ''
  });

  useEffect(() => {
    fetchCompanyDetails();
  }, [companyId]);

  const fetchCompanyDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [companyRes, usersRes, promotionsRes, assignedRes] = await Promise.all([
        axios.get(`${API}/api/admin/companies/${companyId}`, { headers }),
        axios.get(`${API}/api/admin/companies/${companyId}/users`, { headers }),
        axios.get(`${API}/api/admin/promotions`, { headers }).catch(() => ({ data: { promotions: [] } })),
        axios.get(`${API}/api/admin/tenants/${companyId}/promotions`, { headers }).catch(() => ({ data: { promotions: [] } }))
      ]);
      
      setCompany(companyRes.data);
      setUsers(usersRes.data.users || []);
      setAvailablePromotions(promotionsRes.data.promotions || []);
      setAssignedPromotions(assignedRes.data.promotions || []);
      setEditForm({
        company_name: companyRes.data.company_name || '',
        plan: companyRes.data.plan || 'professional',
        seats_included: companyRes.data.seats_included || PLANS[companyRes.data.plan]?.seatsIncluded || 5
      });
      setCompSettings({
        is_comped: companyRes.data.is_comped || false,
        comp_until: companyRes.data.comp_until || '',
        comp_forever: companyRes.data.comp_forever || false,
        comp_reason: companyRes.data.comp_reason || ''
      });
    } catch (error) {
      console.error('Failed to fetch company:', error);
      toast.error('Failed to load company details');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCompany = async () => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/admin/companies/${companyId}`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Company updated successfully!');
      setEditing(false);
      fetchCompanyDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update company');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddUser = async () => {
    if (!newUser.email || !newUser.password) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/admin/companies/${companyId}/users`, newUser, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User added successfully!');
      setShowAddUserDialog(false);
      setNewUser({ name: '', email: '', password: '', role: 'user' });
      fetchCompanyDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSuspendUser = async (userId, isSuspended) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/admin/users/${userId}/suspend`, 
        { is_suspended: !isSuspended },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(isSuspended ? 'User unsuspended' : 'User suspended');
      fetchCompanyDetails();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/admin/users/${userToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User deleted');
      setShowDeleteDialog(false);
      setUserToDelete(null);
      fetchCompanyDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateComp = async () => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/admin/companies/${companyId}/comp`, {
        is_comped: compSettings.is_comped,
        comp_until: compSettings.comp_forever ? null : compSettings.comp_until,
        comp_forever: compSettings.comp_forever,
        comp_reason: compSettings.comp_reason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Comp settings updated!');
      setShowCompDialog(false);
      fetchCompanyDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update comp settings');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAssignPromotion = async () => {
    if (!selectedPromotion) {
      toast.error('Please select a promotion');
      return;
    }
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/admin/promotions/${selectedPromotion}/assign`, {
        tenant_id: companyId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Promotion assigned successfully!');
      setShowCouponDialog(false);
      setSelectedPromotion('');
      fetchCompanyDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign promotion');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevokePromotion = async (assignmentId) => {
    if (!confirm('Are you sure you want to revoke this promotion?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/api/admin/promotion-assignments/${assignmentId}`, {
        is_revoked: true
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Promotion revoked');
      fetchCompanyDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke promotion');
    }
  };

  const handleResetPassword = async (userId, userEmail) => {
    if (!confirm(`Send password reset email to ${userEmail}?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`${API}/api/admin/users/${userId}/reset-password`, 
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Password reset email sent to ${response.data.email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send password reset email');
    }
  };

  // Calculate billing
  const calculateBilling = () => {
    if (!company) return { base: 0, extra: 0, total: 0 };
    
    const plan = PLANS[company.plan] || PLANS.professional;
    const extraUsers = Math.max(0, users.length - plan.seatsIncluded);
    const extraCost = extraUsers * plan.extraSeatPrice;
    
    if (company.is_comped) {
      return { base: plan.basePrice, extra: extraCost, total: 0, comped: true };
    }
    
    return { base: plan.basePrice, extra: extraCost, total: plan.basePrice + extraCost };
  };

  const billing = calculateBilling();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!company) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-400">Company not found</p>
        <Button variant="outline" onClick={() => navigate(`${ADMIN_BASE_URL}/companies`)} className="mt-4">
          Back to Companies
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`${ADMIN_BASE_URL}/companies`)}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
      </div>

      {/* Comp Status Banner - Prominent display */}
      {company.is_comped && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 bg-gradient-to-r from-green-500/20 via-emerald-500/20 to-teal-500/20 border border-green-500/30 rounded-xl"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-green-500/30 flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-green-400 flex items-center gap-2">
                  <Gift className="w-5 h-5" />
                  Complimentary Account
                </h3>
                <p className="text-sm text-slate-300">
                  {company.comp_forever 
                    ? 'This company has permanent complimentary access' 
                    : `Comped until ${company.comp_until ? new Date(company.comp_until).toLocaleDateString() : 'N/A'}`}
                </p>
                {company.comp_reason && (
                  <p className="text-xs text-slate-400 mt-1">Reason: {company.comp_reason}</p>
                )}
              </div>
            </div>
            <Button 
              variant="outline" 
              onClick={() => setShowCompDialog(true)}
              className="border-green-500/50 text-green-400 hover:bg-green-500/20"
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit Comp Status
            </Button>
          </div>
        </motion.div>
      )}

      {/* Company Info Card */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-blue-500/20 flex items-center justify-center">
                <Building2 className="w-7 h-7 text-blue-500" />
              </div>
              <div>
                {editing ? (
                  <Input
                    value={editForm.company_name}
                    onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-xl font-bold"
                  />
                ) : (
                  <CardTitle className="text-2xl text-white flex items-center gap-3">
                    {company.company_name}
                    {company.is_comped && (
                      <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-xs">
                        <Gift className="w-3 h-3 mr-1" />
                        COMPED
                      </Badge>
                    )}
                  </CardTitle>
                )}
                <CardDescription className="flex items-center gap-2 mt-1">
                  <span className="font-mono text-xs">{companyId}</span>
                </CardDescription>
              </div>
            </div>
            <div className="flex gap-2">
              {editing ? (
                <>
                  <Button variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
                  <Button onClick={handleSaveCompany} disabled={submitting} className="bg-blue-600 hover:bg-blue-700">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" />Save</>}
                  </Button>
                </>
              ) : (
                <>
                  <Button 
                    variant="outline" 
                    onClick={() => setShowCompDialog(true)}
                    className={company.is_comped ? "border-green-500/50 text-green-400 hover:bg-green-500/20" : ""}
                  >
                    <Gift className="w-4 h-4 mr-2" />
                    {company.is_comped ? 'Edit Comp' : 'Add Comp'}
                  </Button>
                  <Button variant="outline" onClick={() => setEditing(true)}>
                    <Edit className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div>
              <Label className="text-slate-500">Plan</Label>
              {editing ? (
                <Select
                  value={editForm.plan}
                  onValueChange={(value) => setEditForm({ ...editForm, plan: value, seats_included: PLANS[value].seatsIncluded })}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="professional">Professional ($299/mo)</SelectItem>
                    <SelectItem value="enterprise">Enterprise ($599/mo)</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-white font-medium capitalize">{company.plan}</p>
              )}
            </div>
            <div>
              <Label className="text-slate-500">Seats Included</Label>
              {editing ? (
                <Input
                  type="number"
                  value={editForm.seats_included}
                  onChange={(e) => setEditForm({ ...editForm, seats_included: parseInt(e.target.value) })}
                  className="bg-slate-800 border-slate-700 mt-1"
                />
              ) : (
                <p className="text-white font-medium">{company.seats_included || PLANS[company.plan]?.seatsIncluded}</p>
              )}
            </div>
            <div>
              <Label className="text-slate-500">Current Users</Label>
              <p className="text-white font-medium">{users.length}</p>
            </div>
            <div>
              <Label className="text-slate-500">Created</Label>
              <p className="text-white font-medium">{new Date(company.created_at).toLocaleDateString()}</p>
            </div>
          </div>

          {/* Billing Summary */}
          <div className="mt-6 p-4 bg-slate-800/50 rounded-lg">
            <h4 className="text-sm font-medium text-slate-400 mb-3">Monthly Billing</h4>
            <div className="grid grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-slate-500">Base Plan</p>
                <p className="text-lg font-bold text-white">${billing.base}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Extra Users ({Math.max(0, users.length - (company.seats_included || PLANS[company.plan]?.seatsIncluded))})</p>
                <p className="text-lg font-bold text-white">${billing.extra}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Total</p>
                {billing.comped ? (
                  <p className="text-lg font-bold text-green-400">$0 <span className="text-xs">(Comped)</span></p>
                ) : (
                  <p className="text-lg font-bold text-blue-400">${billing.total}/mo</p>
                )}
              </div>
              <div>
                <p className="text-xs text-slate-500">Per Extra Seat</p>
                <p className="text-lg font-bold text-white">${PLANS[company.plan]?.extraSeatPrice || 18}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Comp Status Card - Dedicated Section */}
      <Card className={`border ${company.is_comped ? 'bg-green-500/5 border-green-500/30' : 'bg-slate-900 border-slate-800'}`}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg ${company.is_comped ? 'bg-green-500/20' : 'bg-slate-800'} flex items-center justify-center`}>
                <Gift className={`w-5 h-5 ${company.is_comped ? 'text-green-400' : 'text-slate-500'}`} />
              </div>
              <div>
                <CardTitle className="text-white flex items-center gap-2">
                  Complimentary Status
                  {company.is_comped ? (
                    <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Active
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-slate-500 border-slate-700">
                      Not Comped
                    </Badge>
                  )}
                </CardTitle>
                <CardDescription>
                  {company.is_comped 
                    ? 'This company has complimentary access and is not being billed' 
                    : 'Enable complimentary access to waive billing for this company'}
                </CardDescription>
              </div>
            </div>
            <Button 
              onClick={() => setShowCompDialog(true)}
              className={company.is_comped 
                ? "bg-green-600 hover:bg-green-700" 
                : "bg-slate-700 hover:bg-slate-600"}
            >
              {company.is_comped ? (
                <>
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Comp Status
                </>
              ) : (
                <>
                  <Gift className="w-4 h-4 mr-2" />
                  Enable Comp
                </>
              )}
            </Button>
          </div>
        </CardHeader>
        {company.is_comped && (
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-green-500/10 rounded-lg">
              <div>
                <Label className="text-slate-400 text-xs">Comp Type</Label>
                <p className="text-white font-medium flex items-center gap-2 mt-1">
                  {company.comp_forever ? (
                    <>
                      <Sparkles className="w-4 h-4 text-green-400" />
                      Permanent (Forever)
                    </>
                  ) : (
                    <>
                      <Clock className="w-4 h-4 text-amber-400" />
                      Time-Limited
                    </>
                  )}
                </p>
              </div>
              <div>
                <Label className="text-slate-400 text-xs">Expires</Label>
                <p className="text-white font-medium mt-1">
                  {company.comp_forever 
                    ? 'Never' 
                    : company.comp_until 
                      ? new Date(company.comp_until).toLocaleDateString() 
                      : 'Not set'}
                </p>
              </div>
              <div>
                <Label className="text-slate-400 text-xs">Monthly Savings</Label>
                <p className="text-green-400 font-bold text-lg mt-1">
                  ${billing.base + billing.extra}
                </p>
              </div>
            </div>
            {company.comp_reason && (
              <div className="mt-4 p-3 bg-slate-800/50 rounded-lg">
                <Label className="text-slate-400 text-xs">Reason for Comp</Label>
                <p className="text-slate-300 mt-1">{company.comp_reason}</p>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Promotions Section */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Tag className="w-5 h-5 text-purple-500" />
              <CardTitle className="text-white">Active Promotions</CardTitle>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => setShowCouponDialog(true)}
                className="border-purple-600 text-purple-400 hover:bg-purple-600/20"
              >
                <Tag className="w-4 h-4 mr-2" />
                Add Coupon
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Active Promotions */}
          <div className="space-y-3">
            {assignedPromotions.length === 0 ? (
              <p className="text-sm text-slate-500">No active promotions assigned to this company</p>
            ) : (
              <div className="space-y-2">
                {assignedPromotions.map((promo) => (
                  <div 
                    key={promo.assignment_id || promo.id} 
                    className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                        <Tag className="w-5 h-5 text-purple-400" />
                      </div>
                      <div>
                        <p className="font-medium text-white">{promo.name || promo.code}</p>
                        <p className="text-xs text-slate-400">
                          {promo.percent_off ? `${promo.percent_off}% off` : 
                           promo.amount_off_cents ? `$${(promo.amount_off_cents / 100).toFixed(2)} off` :
                           promo.free_seat_count ? `${promo.free_seat_count} free seats` : 'Discount'}
                          {promo.effective_end_at && ` • Expires ${new Date(promo.effective_end_at).toLocaleDateString()}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {promo.is_valid !== false && (
                        <Badge variant="outline" className="bg-green-500/10 text-green-400 border-green-500/30">
                          Active
                        </Badge>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRevokePromotion(promo.assignment_id || promo.id)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Users Section */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Users className="w-5 h-5 text-blue-500" />
              <CardTitle className="text-white">Users ({users.length})</CardTitle>
            </div>
            <Button onClick={() => setShowAddUserDialog(true)} className="bg-blue-600 hover:bg-blue-700">
              <UserPlus className="w-4 h-4 mr-2" />
              Add User
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800">
                <TableHead>User</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Last Login</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id} className="border-slate-800">
                  <TableCell>
                    <div>
                      <p className="font-medium text-white">{user.name || 'Unnamed'}</p>
                      <p className="text-sm text-slate-500">{user.email}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="capitalize">
                      {user.role === 'admin' && <Crown className="w-3 h-3 mr-1" />}
                      {user.role}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {user.is_suspended ? (
                      <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                        <Ban className="w-3 h-3 mr-1" />
                        Suspended
                      </Badge>
                    ) : user.is_active === false ? (
                      <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                        <XCircle className="w-3 h-3 mr-1" />
                        Inactive
                      </Badge>
                    ) : (
                      <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Active
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-slate-400">
                    {new Date(user.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-slate-400">
                    {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-slate-800 border-slate-700">
                        <DropdownMenuItem
                          onClick={() => handleResetPassword(user.id, user.email)}
                          className="text-slate-200 hover:bg-slate-700 cursor-pointer"
                        >
                          <Key className="w-4 h-4 mr-2" />
                          Send Password Reset Email
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleSuspendUser(user.id, user.is_suspended)}
                          className="text-slate-200 hover:bg-slate-700 cursor-pointer"
                        >
                          {user.is_suspended ? (
                            <>
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Unsuspend
                            </>
                          ) : (
                            <>
                              <Ban className="w-4 h-4 mr-2" />
                              Suspend
                            </>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator className="bg-slate-700" />
                        <DropdownMenuItem
                          onClick={() => {
                            setUserToDelete(user);
                            setShowDeleteDialog(true);
                          }}
                          className="text-red-400 hover:bg-red-500/20 cursor-pointer"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete User
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
              {users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                    No users in this company
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add User Dialog */}
      <Dialog open={showAddUserDialog} onOpenChange={setShowAddUserDialog}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white">Add User to {company.company_name}</DialogTitle>
            <DialogDescription>
              Create a new user account for this company
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                placeholder="Jane Doe"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                className="bg-slate-800 border-slate-700"
              />
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                placeholder="user@company.com"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                className="bg-slate-800 border-slate-700"
              />
            </div>
            <div className="space-y-2">
              <Label>Password *</Label>
              <Input
                type="password"
                placeholder="••••••••"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                className="bg-slate-800 border-slate-700"
              />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select
                value={newUser.role}
                onValueChange={(value) => setNewUser({ ...newUser, role: value })}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddUserDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddUser}
              disabled={submitting}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Comp Settings Dialog */}
      <Dialog open={showCompDialog} onOpenChange={setShowCompDialog}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Gift className="w-5 h-5 text-green-500" />
              Comp Settings
            </DialogTitle>
            <DialogDescription>
              Give this company free access to the platform
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="is_comped"
                checked={compSettings.is_comped}
                onCheckedChange={(checked) => setCompSettings({ ...compSettings, is_comped: checked })}
              />
              <Label htmlFor="is_comped">Enable Complimentary Access</Label>
            </div>

            {compSettings.is_comped && (
              <>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="comp_forever"
                    checked={compSettings.comp_forever}
                    onCheckedChange={(checked) => setCompSettings({ ...compSettings, comp_forever: checked })}
                  />
                  <Label htmlFor="comp_forever">Comp Forever (no expiration)</Label>
                </div>

                {!compSettings.comp_forever && (
                  <div className="space-y-2">
                    <Label>Comp Until Date</Label>
                    <Input
                      type="date"
                      value={compSettings.comp_until}
                      onChange={(e) => setCompSettings({ ...compSettings, comp_until: e.target.value })}
                      className="bg-slate-800 border-slate-700"
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Reason (internal note)</Label>
                  <Input
                    placeholder="e.g., Beta tester, Partner, Friend..."
                    value={compSettings.comp_reason}
                    onChange={(e) => setCompSettings({ ...compSettings, comp_reason: e.target.value })}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCompDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpdateComp}
              disabled={submitting}
              className="bg-green-600 hover:bg-green-700"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Comp Settings'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Coupon/Promotion Dialog */}
      <Dialog open={showCouponDialog} onOpenChange={setShowCouponDialog}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Tag className="w-5 h-5 text-purple-500" />
              Assign Promotion/Coupon
            </DialogTitle>
            <DialogDescription>
              Add a promotion or coupon code to this company
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select Promotion</Label>
              <Select value={selectedPromotion} onValueChange={setSelectedPromotion}>
                <SelectTrigger className="bg-slate-800 border-slate-700">
                  <SelectValue placeholder="Choose a promotion..." />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {availablePromotions.filter(p => p.status === 'ACTIVE').map((promo) => (
                    <SelectItem key={promo.id} value={promo.id}>
                      <div className="flex items-center gap-2">
                        <span>{promo.name || promo.code}</span>
                        <span className="text-xs text-slate-400">
                          ({promo.percent_off ? `${promo.percent_off}% off` : 
                            promo.amount_off_cents ? `$${(promo.amount_off_cents / 100).toFixed(2)} off` :
                            promo.free_seat_count ? `${promo.free_seat_count} free seats` : 'Discount'})
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {selectedPromotion && (
              <div className="p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                {(() => {
                  const promo = availablePromotions.find(p => p.id === selectedPromotion);
                  if (!promo) return null;
                  return (
                    <div className="space-y-1">
                      <p className="font-medium text-purple-400">{promo.name}</p>
                      <p className="text-sm text-slate-400">Code: {promo.code}</p>
                      <p className="text-sm text-slate-400">
                        {promo.percent_off ? `${promo.percent_off}% discount` : 
                         promo.amount_off_cents ? `$${(promo.amount_off_cents / 100).toFixed(2)} discount` :
                         promo.free_seat_count ? `${promo.free_seat_count} free seats included` : 'Promotional discount'}
                      </p>
                      {promo.end_at && (
                        <p className="text-xs text-slate-500">Expires: {new Date(promo.end_at).toLocaleDateString()}</p>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}

            {availablePromotions.filter(p => p.status === 'ACTIVE').length === 0 && (
              <div className="p-3 bg-slate-800 rounded-lg text-center">
                <p className="text-slate-400">No active promotions available</p>
                <p className="text-xs text-slate-500 mt-1">Create promotions in the Promotions management section</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCouponDialog(false); setSelectedPromotion(''); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleAssignPromotion}
              disabled={submitting || !selectedPromotion}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Assign Promotion'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Delete User
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {userToDelete?.name || userToDelete?.email}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleDeleteUser}
              disabled={submitting}
              className="bg-red-600 hover:bg-red-700"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
